import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { get_api as getApi } from '../../../redux/api_funcs';
import { get_api_url as getApiUrl } from '../../../utils/urls';
import PeriodFilter from './PeriodFilter';
import { readPeriodParams, resolvePeriod } from './period_utils';
import './trial_points.css';

const PAGE_SIZE = 50;

// /trial_points — all-teachers points movement for the period in the URL params.
function TrialPointsList() {
  const history = useHistory();
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total_pages: 0, total_count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const urlParams = new URLSearchParams(location.search);
  const page = Math.max(1, parseInt(urlParams.get('page') || '1', 10) || 1);
  const search = urlParams.get('search') || '';
  const { range, customStart, customEnd } = readPeriodParams(location.search);
  const { startDate, endDate } = resolvePeriod(range, customStart, customEnd);

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    const qp = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      page: String(page),
      page_size: String(PAGE_SIZE)
    });
    if (search) qp.append('search', search);
    const url = getApiUrl(`/v2/cms_user/trial_points/summary?${qp.toString()}`, '', true);
    getApi(url, true)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data?.data || {};
        setRows(data.rows || []);
        setPagination(data.pagination || { page: 1, total_pages: 0, total_count: 0 });
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load trial points. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, page, search]);

  const updateParams = (updates) => {
    const p = new URLSearchParams(location.search);
    Object.entries(updates).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
    history.replace({ pathname: location.pathname, search: `?${p.toString()}` });
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    updateParams({ search: searchInput.trim(), page: '' });
  };

  const openDetail = (teacherUuid) => {
    window.open(`/trial_points/${teacherUuid}${location.search}`, '_blank', 'noopener,noreferrer');
  };

  const renderRow = (row) => (
    <div className="tp-row" key={row.teacher_uuid}>
      <div className="tp-col tp-left tp-wide" style={{ fontWeight: 600 }}>
        <a
          href={`/teacherprofile?id=${encodeURIComponent(row.teacher_uuid)}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {row.teacher_name || row.teacher_uuid}
        </a>
      </div>
      <div className="tp-col">{row.starting_balance}</div>
      <div className="tp-col" style={{ color: 'var(--state-success)', fontWeight: 600 }}>
        {row.points_added}
      </div>
      <div className="tp-col" style={{ color: 'var(--state-error)', fontWeight: 600 }}>
        {row.points_deducted}
      </div>
      <div className="tp-col" style={{ color: 'var(--brand-primary)', fontWeight: 600 }}>
        {row.ending_balance}
      </div>
      <div className="tp-col">{row.conversions_1on1}</div>
      <div className="tp-col">{row.conversions_gc}</div>
      <div className="tp-col">
        <button type="button" className="link-btn" onClick={() => openDetail(row.teacher_uuid)}>
          Details
        </button>
      </div>
    </div>
  );

  return (
    <div className="common-page-container">
      <div className="common-page-header">
        <div className="common-page-title">Trial Points</div>
      </div>
      <div>
        <PeriodFilter />
        <form onSubmit={onSearchSubmit} className="tp-search">
          <input
            type="text"
            value={searchInput}
            placeholder="Teacher name"
            onChange={(e) => setSearchInput(e.target.value)}
            style={{
              background: 'var(--input-bg)',
              border: '1px solid var(--input-border)',
              color: 'var(--input-text)',
              padding: '0 14px',
              borderRadius: '10px',
              height: '44px',
              width: '260px',
              fontSize: '15px',
              boxSizing: 'border-box'
            }}
          />
          <button type="submit" className="link-btn" style={{ margin: 0 }}>
            Search
          </button>
        </form>
      </div>
      {error ? <div style={{ color: 'var(--state-error)' }}>{error}</div> : null}
      <div className="tp-table">
        <div className="tp-row tp-head">
          <div className="tp-col tp-left tp-wide">Teacher</div>
          <div className="tp-col">Starting</div>
          <div className="tp-col">Added</div>
          <div className="tp-col">Deducted</div>
          <div className="tp-col">Remaining</div>
          <div className="tp-col">1on1 Conv</div>
          <div className="tp-col">GC Conv</div>
          <div className="tp-col">Actions</div>
        </div>
        {loading ? (
          <div className="tp-row">
            <div className="tp-col tp-left">Loading…</div>
          </div>
        ) : null}
        {!loading && rows.length === 0 ? (
          <div className="tp-row">
            <div className="tp-col tp-left">
              No teachers with trial-points activity in this period
            </div>
          </div>
        ) : null}
        {!loading && rows.map(renderRow)}
      </div>
      {pagination.total_pages > 1 ? (
        <div className="tp-pagination">
          <button
            type="button"
            className="ct-small-btn"
            disabled={page <= 1}
            onClick={() => updateParams({ page: String(page - 1) })}
          >
            Prev
          </button>
          <span>
            Page {pagination.page} of {pagination.total_pages} ({pagination.total_count} teachers)
          </span>
          <button
            type="button"
            className="ct-small-btn"
            disabled={page >= pagination.total_pages}
            onClick={() => updateParams({ page: String(page + 1) })}
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default TrialPointsList;
